/**
 * CardExtendDialog - 카드 연장 확인 다이얼로그 (v4 단순화)
 *
 * 디자인 원칙:
 * 1. 2초 규칙 — 열자마자 핵심 파악
 * 2. 정보 3개 이하 — 인지 부하 최소화
 * 3. 행동 중심 — "이해시키기"보다 "행동하게 하기"
 *
 * @author 조너선아이브 (Designer)
 * @version v4
 * @date 2026-02-17
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
} from '@mui/material';
import { Favorite } from '@mui/icons-material';
import type { CardExtendCost } from '../types/heart';

interface CardExtendDialogProps {
  open: boolean;
  cost: CardExtendCost;
  onConfirm: () => void;
  onCancel: () => void;
  onCharge: () => void;
  isMutual?: boolean;
}

const DAY_NAMES = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];

/**
 * 서버에서 KST로 내려주는 시간을 변환 없이 그대로 파싱하여 포맷
 * e.g. "2026-02-22 11:00:00" → "이번 주 일요일", "오전 11시까지"
 */
const formatExpiresAt = (rawDateStr: string): { dayLine: string; timeLine: string } => {
  const match = rawDateStr.match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2})/);
  if (!match) return { dayLine: '', timeLine: '' };

  const [, year, month, day, hourStr] = match;
  const hours = parseInt(hourStr, 10);

  // 요일 계산: KST 오프셋 명시하여 정확한 요일 획득
  const kstDate = new Date(`${year}-${month}-${day}T00:00:00+09:00`);

  // 자정(00시)인 경우: 전날 자정까지를 의미 (e.g. 월요일 00:00 → 일요일 자정)
  if (hours === 0) {
    const prevDate = new Date(kstDate.getTime() - 24 * 60 * 60 * 1000);
    const dayName = DAY_NAMES[prevDate.getDay()];
    return {
      dayLine: `이번 주 ${dayName}`,
      timeLine: '자정까지',
    };
  }

  const dayName = DAY_NAMES[kstDate.getDay()];
  const ampm = hours < 12 ? '오전' : '오후';
  const displayHour = hours <= 12 ? hours : hours - 12;

  return {
    dayLine: `이번 주 ${dayName}`,
    timeLine: `${ampm} ${displayHour}시까지`,
  };
};

export const CardExtendDialog: React.FC<CardExtendDialogProps> = ({
  open,
  cost,
  onConfirm,
  onCancel,
  onCharge,
  isMutual = false,
}) => {
  const hasEnoughHearts = cost.beforeBalance >= cost.hearts;
  const canExtend = cost.currentExtensions < cost.maxExtensions;

  // 연장 불가 (이미 1회 사용)
  if (!canExtend) {
    return (
      <Dialog
        open={open}
        onClose={onCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography
            sx={{ fontSize: 24, fontWeight: 700, color: '#0D1F3C', mb: 2 }}
          >
            이미 연장을 사용하셨어요
          </Typography>
          <Typography sx={{ fontSize: 20, color: '#8896AB', mb: 3 }}>
            카드 연장은 1회만 가능합니다.
          </Typography>
          <Button
            variant="contained"
            onClick={onCancel}
            fullWidth
            sx={{
              height: 64,
              fontSize: 24,
              fontWeight: 700,
              borderRadius: 3,
              bgcolor: '#8896AB',
              '&:hover': { bgcolor: '#6B7A8D' },
            }}
          >
            확인
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}
      aria-labelledby="card-extend-dialog-title"
      aria-modal="true"
    >
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* 제목 */}
          <Typography
            id="card-extend-dialog-title"
            sx={{
              fontSize: 28,
              fontWeight: 700,
              color: '#0D1F3C',
              textAlign: 'center',
            }}
          >
            카드 연장하기
          </Typography>

          {/* 핵심 정보 카드 */}
          {hasEnoughHearts ? (
            <Box
              sx={{
                bgcolor: '#F0F3F8',
                border: '1px solid #E2E8F0',
                borderRadius: 3,
                p: 3,
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{ fontSize: 26, fontWeight: 700, color: '#0D1F3C' }}
              >
                이번 주 일요일
              </Typography>
              <Typography
                sx={{ fontSize: 26, fontWeight: 700, color: '#0D1F3C' }}
              >
                자정까지
              </Typography>
              <Typography
                sx={{ fontSize: 26, fontWeight: 700, color: '#0D1F3C', mb: 1.5 }}
              >
                연장됩니다
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <Favorite sx={{ fontSize: 20, color: '#8896AB' }} />
                <Typography
                  sx={{ fontSize: 20, fontWeight: 600, color: '#8896AB' }}
                >
                  하트 {cost.hearts}개 사용
                </Typography>
              </Box>
              {isMutual && (
                <Typography sx={{
                  fontSize: 14, color: '#7A5500', mt: 1.5,
                  lineHeight: 1.5, textAlign: 'center',
                }}>
                  연장하면 내 카드가 이번 주 일요일까지 유지돼요.<br />
                  상대방이 연장하지 않아도 대화는 계속할 수 있어요.
                </Typography>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                bgcolor: '#FFF5F0',
                border: '1px solid #FFD5C2',
                borderRadius: 3,
                p: 3,
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{ fontSize: 26, fontWeight: 700, color: '#FF6B35', mb: 1.5 }}
              >
                하트가 부족해요
              </Typography>
              <Typography
                sx={{ fontSize: 20, fontWeight: 500, color: '#8896AB' }}
              >
                연장하려면 하트 {Math.max(0, cost.hearts - cost.beforeBalance)}개가 더 필요해요
              </Typography>
            </Box>
          )}

          {/* 버튼 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {hasEnoughHearts ? (
              <>
                <Button
                  variant="contained"
                  onClick={onConfirm}
                  fullWidth
                  autoFocus
                  sx={{
                    height: 64,
                    fontSize: 24,
                    fontWeight: 700,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #0D1F3C 0%, #1A3461 100%)',
                    boxShadow: 4,
                    '&:hover': {
                      boxShadow: 8,
                      background: 'linear-gradient(135deg, #1A3461 0%, #071224 100%)',
                    },
                    '&:active': { transform: 'scale(0.98)' },
                  }}
                >
                  하트 {cost.hearts}개로 연장하기
                </Button>
                <Button
                  variant="outlined"
                  onClick={onCancel}
                  fullWidth
                  sx={{
                    height: 56,
                    fontSize: 20,
                    fontWeight: 500,
                    borderRadius: 3,
                    color: '#8896AB',
                    borderColor: '#E2E8F0',
                    '&:hover': { bgcolor: '#F0F3F8', borderColor: '#8896AB' },
                    '&:active': { transform: 'scale(0.98)' },
                  }}
                >
                  다음에 할게요
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  onClick={onCharge}
                  fullWidth
                  autoFocus
                  sx={{
                    height: 64,
                    fontSize: 24,
                    fontWeight: 700,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #C9A86C 0%, #B8943F 100%)',
                    boxShadow: 4,
                    '&:hover': {
                      boxShadow: 8,
                      background: 'linear-gradient(135deg, #A8864A 0%, #9A7030 100%)',
                    },
                    '&:active': { transform: 'scale(0.98)' },
                  }}
                >
                  하트 충전하기
                </Button>
                <Button
                  variant="outlined"
                  onClick={onCancel}
                  fullWidth
                  sx={{
                    height: 56,
                    fontSize: 20,
                    fontWeight: 500,
                    borderRadius: 3,
                    color: '#8896AB',
                    borderColor: '#E2E8F0',
                    '&:hover': { bgcolor: '#F0F3F8', borderColor: '#8896AB' },
                    '&:active': { transform: 'scale(0.98)' },
                  }}
                >
                  취소
                </Button>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CardExtendDialog;
